import { useEffect } from 'react';

import { getContext } from '@/context';
import { getAppStore, setAppStore } from '@/store';
import { refreshMonetization } from '@/features/credits';
import type { CreditInfo } from '@/types/monetization';
import useAuth from './useAuth';

type CreditsState = {
  areCreditsAvailable?: boolean;
  areCreditsFetched?: boolean;
  areCreditsLoading?: boolean;
  creditsInfo?: Partial<CreditInfo> | null;
  creditInfo?: Partial<CreditInfo> | null;
};

const normalizeCreditsState = (credits: CreditsState) => {
  if (credits.areCreditsAvailable === false) {
    return {
      ...credits,
      areCreditsFetched: true,
      areCreditsLoading: false,
    };
  }

  if (credits.areCreditsAvailable === true) {
    return {
      ...credits,
      areCreditsFetched: true,
      areCreditsLoading: false,
    };
  }

  return credits;
};

const useCreditsSubscription = (enabled = true) => {
  const { isAuthorized } = useAuth();

  useEffect(() => {
    // Reset credits state when user is not authorized
    if (!isAuthorized) {
      setAppStore({ areCreditsAvailable: null, creditsInfo: null });
      return;
    }

    // Only subscribe when enabled
    if (!enabled) {
      return;
    }

    try {
      const context = getContext();
      refreshMonetization();

      const unsubscribe = context.monetization.credits.subscribe(
        (value: CreditsState) => {
          const info = value?.creditsInfo ?? value?.creditInfo;
          if (info) {
            setAppStore({
              creditsInfo: { ...getAppStore().creditsInfo, ...info },
            });
          }

          if (value?.areCreditsAvailable === undefined) {
            return;
          }

          const normalized = normalizeCreditsState({ ...value });

          const nextAvailability =
            typeof normalized.areCreditsAvailable === 'boolean'
              ? normalized.areCreditsAvailable
              : null;

          setAppStore({ areCreditsAvailable: nextAvailability });
        },
        { needPrevious: true }
      );

      const unsubscribeResume = context.handlers.onResume(refreshMonetization);

      return () => {
        unsubscribe?.();
        unsubscribeResume?.();
      };
    } catch {
      // SDK not ready
    }
  }, [enabled, isAuthorized]);
};

export default useCreditsSubscription;
